/**
 * SubAttendance — admin/manager log of subcontractor check-ins (/sub-attendance).
 * Date-range log grouped by day with GPS on-site flags, plus PIN setup (admin).
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { HardHat, MapPin, Copy } from "lucide-react";

const mtTime = (ts) => ts ? new Date(ts).toLocaleTimeString("en-US", { timeZone: "America/Denver", hour: "numeric", minute: "2-digit" }) : "—";
const mtDate = (ts) => new Date(ts).toLocaleDateString("en-US", { timeZone: "America/Denver", weekday: "short", month: "short", day: "numeric", year: "numeric" });
const mtDayKey = (ts) => new Date(ts).toLocaleDateString("en-CA", { timeZone: "America/Denver" });

const GpsFlag = ({ onsite, dist }) => {
  if (onsite === true) return <span className="text-emerald-700 font-bold">✓ On site</span>;
  if (onsite === false) return <span className="text-amber-600 font-bold">⚠ {dist} mi from site</span>;
  return <span className="text-zinc-400">—</span>;
};

export default function SubAttendance() {
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [range, setRange] = useState(() => {
    const end = new Date().toISOString().slice(0, 10);
    const s = new Date(); s.setDate(s.getDate() - 7);
    return { start: s.toISOString().slice(0, 10), end };
  });
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const subclockUrl = `${window.location.origin}/subclock`;

  const load = () => {
    api.get(`/subclock/log?start=${range.start}&end=${range.end}`).then((r) => setRows(r.data || [])).catch(() => {});
    api.get("/subclock/vendors").then((r) => setVendors(r.data || [])).catch(() => {});
  };
  useEffect(load, [range.start, range.end]); // eslint-disable-line

  const byDay = useMemo(() => {
    const g = {};
    for (const r of rows) (g[mtDayKey(r.check_in)] = g[mtDayKey(r.check_in)] || []).push(r);
    return Object.entries(g).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [rows]);

  const savePin = async (vendorId, pin) => {
    try { await api.put(`/subclock/vendor-setup/${vendorId}`, { subclock_pin: pin }); toast.success("PIN saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="sub-attendance-page">
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <HardHat className="w-6 h-6 text-blue-700" />
        <h1 className="font-heading text-2xl font-black tracking-tight">Sub Attendance</h1>
        <div className="ml-auto flex items-center gap-2">
          <input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="h-9 px-2 border border-zinc-300 rounded-sm text-xs" data-testid="sub-att-start" />
          <input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="h-9 px-2 border border-zinc-300 rounded-sm text-xs" data-testid="sub-att-end" />
        </div>
      </div>
      <div className="text-xs text-zinc-500 mb-6 flex items-center gap-2 flex-wrap">
        Sub foremen check in at: <span className="font-mono text-[11px] bg-zinc-100 border border-zinc-200 rounded px-1.5 py-0.5">{subclockUrl}</span>
        <button data-testid="copy-subclock-link" onClick={() => { navigator.clipboard.writeText(subclockUrl); toast.success("Link copied"); }}
          className="inline-flex items-center gap-1 text-blue-700 font-bold hover:underline"><Copy className="w-3 h-3" /> Copy link</button>
        <span className="text-zinc-400">· A daily digest of check-ins emails you at 6:00 PM MT.</span>
      </div>

      {byDay.length === 0 && (
        <div className="bg-white border border-zinc-200 rounded-sm p-8 text-center text-sm text-zinc-500 mb-6" data-testid="no-sub-punches">
          No sub check-ins in this date range.
        </div>
      )}

      {byDay.map(([day, list]) => (
        <div key={day} className="bg-white border border-zinc-200 rounded-sm mb-4" data-testid={`sub-day-${day}`}>
          <div className="px-4 py-2.5 border-b border-zinc-100 font-bold text-sm">{mtDate(list[0].check_in)}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-zinc-50">
                <tr>
                  {["Job", "Subcontractor", "Foreman", "In (MT)", "Arrival GPS", "Out (MT)", "Departure GPS", "Hrs"].map((h) => (
                    <th key={h} className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-zinc-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id} className="border-t border-zinc-50">
                    <td className="px-3 py-2 font-medium">{r.deal_title}</td>
                    <td className="px-3 py-2">{r.vendor_name}</td>
                    <td className="px-3 py-2">{r.foreman_name || "—"}</td>
                    <td className="px-3 py-2 font-mono">{mtTime(r.check_in)}</td>
                    <td className="px-3 py-2"><GpsFlag onsite={r.on_site_in} dist={r.distance_in_mi} /></td>
                    <td className="px-3 py-2 font-mono">{r.check_out ? mtTime(r.check_out) : <span className="text-emerald-700 font-bold">on site</span>}</td>
                    <td className="px-3 py-2"><GpsFlag onsite={r.on_site_out} dist={r.distance_out_mi} /></td>
                    <td className="px-3 py-2 font-mono">{r.check_out ? r.hours?.toFixed?.(2) ?? r.hours : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {isAdmin && (
        <div className="bg-white border border-zinc-200 rounded-sm p-4 mt-8 max-w-xl" data-testid="sub-pin-setup">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-1">Subcontractor Setup — Check-In PIN</div>
          <div className="text-xs text-zinc-500 mb-3 flex items-center gap-1"><MapPin className="w-3 h-3" /> GPS on-site flags need the job&apos;s property address on file.</div>
          {vendors.length === 0 && <div className="text-xs text-zinc-500">No subcontractors yet — add them under Vendors → kind &quot;Subcontractor&quot;.</div>}
          <div className="space-y-2">
            {vendors.map((v) => (
              <div key={v.id} className="flex items-center gap-3">
                <div className="flex-1 text-sm font-medium">{v.name}</div>
                <input defaultValue={v.pin} placeholder="PIN" maxLength={8}
                  onBlur={(e) => e.target.value !== v.pin && savePin(v.id, e.target.value)}
                  data-testid={`sub-pin-${v.id}`} className="h-8 w-28 px-2 border border-zinc-300 rounded-sm font-mono text-sm" />
                {!v.pin_set && <span className="text-[10px] text-amber-600 font-bold uppercase">No PIN</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
